import { History } from 'history';
import { Dispatch } from 'redux';

import { ROUTES } from '../../../../routes';
import {
    ModalVariants, openModal
} from '../../../components';
import { fetchNotifications } from '../../../components/notifications';
import {
    setAppLoading, updateSelectedClassroom
} from '../../../state/app.actions';
import {
    CurrentClassroom, LoadingState
} from '../../../state/app.types';

type ClassroomGroupParams = CurrentClassroom;

/*
Some keys passed through portal comms cause issues removing token form URL.
This list manages any keys passed to portalComms that break application.
 */
const removedKeys = ['authToken'];

export const parseMessage = (event: MessageEvent) => {
    try {
        return JSON.parse(event.data);
    } catch (e) {
        // angular portal behaviour:
        // If it is a parsing error, just don't continue to check the message - it's not for us
        if (e instanceof SyntaxError) {
            return null;
        } else {
            throw e;
        }
    }
};

export const convertObjectToSearchParams = (data: Record<string, string | number | boolean>): string => {
    // any required to allow expected working types.
    // @see related issue on the Typescript type https://github.com/microsoft/TypeScript/issues/32951
    return new URLSearchParams(data as any).toString();
};

export const removeSelectedKeys = (params: CurrentClassroom, removedKeys: string[]) => {
    for (const key in params) {
        if (key) {
            const k = key as keyof ClassroomGroupParams;

            if (removedKeys.includes(k)) delete params[k];
        }
    }

    return params;
};

type ErrorOptions = {
    dispatch: Dispatch
};

export const handleError = ({ dispatch }: ErrorOptions) => {
    return (
        dispatch(openModal({ variant: ModalVariants.error }))
    );
};

type SetCurrentClassGroupOptions = {
    params: CurrentClassroom
    dispatch: Dispatch
};

export const handleSetCurrentClassGroup = ({
    params,
    dispatch
}: SetCurrentClassGroupOptions) => {
    params = removeSelectedKeys(params, removedKeys);

    assertCurrentClassroom(params);
    dispatch(updateSelectedClassroom(params));
};

type ModuleChangeOptions = {
    moduleId: number
    history: History
    dispatch: Dispatch
    params?: ClassroomGroupParams
};

export const handleModuleChange = ({
    moduleId,
    history,
    dispatch,
    params
}: ModuleChangeOptions) => {
    if (moduleId !== undefined) {
        if (params) {
            params = removeSelectedKeys(params, removedKeys);
            dispatch(updateSelectedClassroom(params));
        }

        history.push(`${ROUTES.content}/${moduleId}`);
    }
};

type MakeAPICallOptions = {
    dispatch: Dispatch
    params: {
        apiType: string
    }
};

export const handleMakeAPICall = ({
    dispatch, params
}: MakeAPICallOptions) => {
    switch (params.apiType) {
    case 'Notification': {
        dispatch(fetchNotifications());
        break;
    }
    default: break;
    }
};

type NavigateToLandingRouteOptions = {
    moduleId: string
    history: History
};

export const handleNavigateToLandingRoute = ({
    history, moduleId
}: NavigateToLandingRouteOptions) => {
    history.push(`${ROUTES.landing}/${moduleId}`);
};

export function assertCurrentClassroom (
    value: any
): asserts value is CurrentClassroom {
    if (
        typeof value?.sourceModuleId !== 'string' && typeof value?.sourceModuleId !== 'undefined' &&
        typeof value?.groupId !== 'string' && typeof value?.groupId !== 'undefined' &&
        typeof value?.classId !== 'string'
    ) {
        console.error('assertCurrentClassroom', value);
        throw new Error('Invalid CurrentClassroom message params');
    }
}

type LoaderSpinnerOptions = {
    dispatch: Dispatch
    message: boolean
};

export function handleLoaderSpinner ({
    dispatch, message
}: LoaderSpinnerOptions) {
    const updatedAppLoading = message ? LoadingState.contentLoading : LoadingState.loaded;

    dispatch(setAppLoading(updatedAppLoading));
}

type DucsEventModalOptions = {
    dispatch: Dispatch
    message: Array<string>
};

export const handleOpenDucsEventModal = ({
    dispatch, message
} : DucsEventModalOptions) => {
    dispatch(openModal({
        variant: ModalVariants.ducsContent,
        modalProps: { message }
    }));
};
